import React from 'react'
import { Table, Button, Tag, Badge } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { RFQ } from '../types/rfq.types'
import { formatINR, formatIST } from '../../../shared/utils/formatters'

interface RFQsTableProps {
    rfqs: RFQ[]
    onRowClick: (rfq: RFQ) => void
}

export const RFQsTable: React.FC<RFQsTableProps> = ({ rfqs, onRowClick }) => {
    const columns: ColumnsType<RFQ> = [
        {
            title: 'Ref ID',
            dataIndex: 'reference_id',
            key: 'reference_id',
            width: 150,
            render: (text: string, record) => <strong>{text || `#${record.id}`}</strong>,
        },
        {
            title: 'Customer',
            dataIndex: 'customer_name',
            key: 'customer',
            render: (text: string, record) => text || `Customer #${record.customer_id}`,
        },
        {
            title: 'Product',
            dataIndex: 'product_id',
            key: 'product',
            render: (id: number, record) => record.product_name || `Product #${id}`,
        },
        {
            title: 'Quantity',
            key: 'quantity',
            render: (_, record) => `${record.quantity} ${record.unit}`,
        },
        {
            title: 'Pincode',
            dataIndex: 'delivery_pincode',
            key: 'pincode',
            width: 100,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => {
                let color = 'default';
                switch (status) {
                    case 'draft': color = 'default'; break;
                    case 'open': color = 'processing'; break;
                    case 'bidding': color = 'warning'; break;
                    case 'closed': color = 'error'; break; // or default
                    case 'awarded': color = 'success'; break;
                    case 'accepted': color = 'success'; break;
                    case 'cancelled': color = 'error'; break;
                    case 'expired': color = 'error'; break;
                }
                return <Badge status={color as any} text={status.toUpperCase()} />;
            },
        },
        {
            title: 'Bids',
            dataIndex: 'bids_count',
            key: 'bidsCount',
            align: 'center',
            render: (count: number) => (
                <Tag color={count > 0 ? 'blue' : 'default'}>
                    {count || 0}
                </Tag>
            ),
        },
        {
            title: 'Best Price',
            dataIndex: 'best_bid_price',
            key: 'bestPrice',
            render: (price: number) => price ? formatINR(price) : '-',
        },
        {
            title: 'Required By',
            dataIndex: 'required_by',
            key: 'requiredBy',
            render: (date: string) => formatIST(date),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button type="link" icon={<EyeOutlined />} onClick={() => onRowClick(record)}>
                    View Details
                </Button>
            ),
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={rfqs}
            rowKey="id"
            onRow={(record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: 'pointer' },
            })}
            pagination={{
                total: rfqs.length,
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} RFQs`,
            }}
        />
    )
}
